import React, { Fragment, useContext, useEffect, useMemo } from "react";
import SaitButton from "../atomic/SaitButton";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import "dayjs/locale/it";
import SaitDialog from "../atomic/SaitDialog";
import { setGlobalError } from "../store/globalMessageReducer";
import { useDispatch, useSelector } from "react-redux";
import i18next from "../localization/i18n";
import EditPanel from "./EditPanel";
import { checkOpen } from "../utils/SaitUtils";
import { TAB } from "../utils/ComponentList";
import { isDayjs } from "dayjs";
import { checkIsRoleAllowed } from "../utils/SecurityUtils";
import { ModuleContext } from "../utils/Constants";

/**
 * _______EditWindow_______
 *
 * @param {string} title - Titolo della window.
 * @param {Array} fields - Campi contenuti nella window.
 * @param {boolean} open - Per aprire la window.
 *
 * __Gestione azioni:__
 * @param {Function} close - Funzione eseguita alla pressione del tasto "Chiudi".
 * @param {Function} onSave - Funzione eseguita alla pressione del tasto "Salva".
 *
 * __Stato:__
 * @param {Object} stateElement - Record di stato della window.
 * @param {Function} setFunction - Setter per aggiornare il record di stato.
 *
 * _______OPZIONALI_______
 *
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} [width="lg"] - Grandezza della window (DEFAULT: lg).
 * @param {number|string} [minHeight] - Altezza minima della window.
 * @param {boolean} [disabled=false] - Per disabilitare il bottone "Salva" (DEFAULT: false).
 * @param {boolean} [disabledAll=false] - Per disabilitare tutti i campi e bottoni della window (DEFAULT: false).
 * @param {"left"|"center"|"right"} [titleTextAlign="center"] - Allineamento del titolo (DEFAULT: center).
 * @param {boolean} [fullScreen=false] - Per rendere la window a schermo intero.
 * @param {boolean} [fullWidth=false] - Per rendere la window a larghezza piena.
 * @param {string} [text] - Testo personalizzato del bottone "Salva".
 * @param {JSX.Element} [endIcon] - Icona del bottone "Salva".
 * @param {string} [color] - Colore del bottone "Salva".
 * @param {Function|Object} [roleSecurity] - Regola o funzione che definisce i ruoli autorizzati ad accedere alla window.
 *
 * __Top buttons:__
 * @param {Object} [topButtons] - Pulsanti aggiuntivi nella parte superiore della window.
 * @param {string} [topButtons.text] - Testo del pulsante.
 * @param {JSX.Element} [topButtons.icon] - Icona del pulsante.
 * @param {Function} [topButtons.function] - Funzione da eseguire al click del pulsante.
 *
 * @returns {JSX.Element}
 */

export default function EditWindow(props) {
	const dispatch = useDispatch();
	const module = useContext(ModuleContext);
	const userPreferences = useSelector((state) => state["auth"]);

	const [attemptSave, setAttemptSave] = React.useState(false);
	const [requiredFields, setRequiredFields] = React.useState([]);

	const stringPropsFields = useMemo(() => JSON.stringify(props.fields), [props.fields]);

	useEffect(() => {
		let newRequiredArray = [];
		JSON.parse(stringPropsFields).forEach((paragraph) => {
			if (paragraph.type === TAB) {
				paragraph.items.forEach((card) => {
					card.items.forEach((item) => {
						if (item.required) {
							newRequiredArray.push(item.name);
						}
					});
				});
			} else {
				paragraph.items.forEach((item) => {
					if (item.required) {
						newRequiredArray.push(item.name);
					}
				});
			}
		});
		setRequiredFields(newRequiredArray);
	}, [stringPropsFields]);

	useEffect(() => {
		if (!checkOpen(props.open)) {
			setAttemptSave(false);
		}
	}, [props.open]);

	const handleClose = () => {
		props.close();
	};

	const getErrorsArray = (input) => {
		let result = [];

		function traverse(node) {
			if (Array.isArray(node)) {
				node.forEach(traverse);
			} else if (Array.isArray(node.items)) {
				node.items.forEach(traverse);
			} else {
				if (node.error) {
					result.push(node);
				}
			}
		}

		traverse(input);
		return result;
	};

	const checkRequiredFields = () => {
		let requiredOk = true;
		requiredFields.some((requiredField) => {
			if (Array.isArray(requiredField)) {
				// Controllo che almeno uno degli storeName della Combo sia valorizzato
				let allComboNull = true;
				requiredField.forEach((reqField) => {
					if (props.stateElement[reqField.storeName] || props.stateElement[reqField.storeName] === 0) {
						allComboNull = false;
					}
				});
				if (allComboNull) {
					requiredOk = false;
					return true;
				}
			} else {
				let requiredFieldValue = props.stateElement[requiredField];
				if (
					typeof requiredFieldValue === "undefined" ||
					requiredFieldValue === null ||
					requiredFieldValue === "" ||
					(isDayjs(requiredFieldValue) && isNaN(requiredFieldValue.toDate().getTime()))
				) {
					requiredOk = false;
					return true;
				}
			}
			return false;
		});

		if (!requiredOk) {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("error_enter_all_required_fields"),
				})
			);
		}

		return requiredOk;
	};

	const checkErrorFields = () => {
		let errorOk = true;
		const errorsFields = getErrorsArray(props.fields);
		if (errorsFields.length > 0) {
			errorOk = false;
		}

		if (!errorOk) {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("there_are_error_fields") + ": " + errorsFields.map((field) => field.label).join(", "),
				})
			);
		}
		return errorOk;
	};

	const handleOnSave = () => {
		setAttemptSave(true);

		if (checkRequiredFields() === false) {
			return false;
		}

		if (checkErrorFields() === false) {
			return false;
		}

		props.onSave();
	};

	return (
		<SaitDialog
			open={checkOpen(props.open)}
			title={props.title}
			topButtons={props.topButtons}
			width={props.width || "lg"}
			minHeight={props.minHeight}
			scroll={props.scroll || "paper"}
			flexDirection="column"
			titleTextAlign={props.titleTextAlign}
			fullScreen={props.fullScreen}
			fullWidth={props.fullWidth}
			content={<EditPanel attemptSave={attemptSave} setFunction={props.setFunction} stateElement={props.stateElement} fields={props.fields} open={props.open} disabledAll={props.disabledAll} />}
			actions={
				<Fragment>
					<SaitButton text={i18next.t("close")} color="error" endIcon={<CancelIcon />} onClick={handleClose} {...props.cancelProps} />
					<div style={{ flexGrow: 1 }} />
					{props.onSave && (
						<SaitButton
							text={props.text || i18next.t("save")}
							endIcon={props.endIcon || <SaveIcon />}
							onClick={handleOnSave}
							color={props.color || "success"}
							{...props.confirmProps}
							disabled={props.disabled || props.disabledAll || !checkIsRoleAllowed(props.roleSecurity, userPreferences)}
						/>
					)}
				</Fragment>
			}
		/>
	);
}
